import React from 'react'
import { useGetBusinessID } from '@marketing-milk/frontend'
import {
  AdCreative,
  DraftCampaign,
  PLACEMENTS_MISS_STORY_MESSAGE,
  resolveOptimizationDefinition,
  SavedAudience,
} from '@marketing-milk/interfaces'
import { Check } from '@mui/icons-material'
import { Button } from '@mui/material'
import { CampaignDetails } from 'components'
import { DataTable } from 'components/DataTable'
import { sortBy } from 'lodash/fp'
import {
  ErrorItem,
  ErrorsIndicator,
} from 'pages/business/campaigns/components/status-indicator/ErrorsIndicator'
import {
  adGroupLabel,
  describeUnavailableAudience,
  getAdCreativeGroups,
  getAudiencesById,
  getCampaignPublishBlockers,
  PublishBlocker,
} from 'pages/business/campaigns/helpers/campaignForm.helpers'
import { useHistory } from 'react-router-dom'

interface Props {
  draftCampaigns?: DraftCampaign[]
  audiences?: SavedAudience[]
  sprintCreatives?: AdCreative[]
  sprintId?: number
  /**
   * The sprint's rejected ads on the review surface, for the route out of a client
   * rejection. Absent when the sprint has no packet — the rejection still reports,
   * just without the second route.
   */
  reviewHref?: string
  isLoadingData?: boolean
  openModal: (
    type: 'creatives' | 'audiences' | 'settings' | 'leadForms',
    draft: DraftCampaign
  ) => void
}
export const DraftCampaignsTable = ({
  draftCampaigns,
  audiences,
  sprintCreatives,
  sprintId,
  reviewHref,
  isLoadingData,
  ...props
}: Props) => {
  const businessId = useGetBusinessID()
  const history = useHistory()

  const fixButton = (
    type: 'creatives' | 'audiences' | 'settings' | 'leadForms',
    draft: DraftCampaign,
    label: string
  ) => (
    <Button variant={'contained'} onClick={() => props.openModal(type, draft)}>
      {label}
    </Button>
  )

  // Each reason the launcher refuses a campaign, in words that say what to fix. The rules
  // themselves live in getCampaignPublishBlockers, so this can't drift behind the publish button.
  const blockerError = (blocker: PublishBlocker, draft: DraftCampaign): ErrorItem => {
    switch (blocker.kind) {
      case 'missingOptimization':
        return {
          title: 'Missing Campaign Optimization',
          message: 'This campaign has no optimization selected, so it cannot be published.',
          details:
            'Both how Meta delivers the campaign and which creatives it can run follow from the objective and performance goal.',
          actionButton: fixButton('settings', draft, 'Select Optimization'),
        }
      case 'missingConversion':
        return {
          title: 'Missing Conversion Event',
          message: 'This campaign optimizes for a conversion but none is selected',
          details: 'Pick which conversion Facebook should optimize for.',
          actionButton: fixButton('settings', draft, 'Open Settings'),
        }
      case 'invalidName':
        return {
          title: 'Campaign Name Needs Fixing',
          message: 'This campaign needs a name under 256 characters',
          details: 'Facebook refuses campaign names of 256 characters or more.',
          actionButton: fixButton('settings', draft, 'Open Settings'),
        }
      case 'invalidBudget':
        return {
          title: 'Budget Out Of Range',
          message: 'The budget must be between $50 and $9,999',
          details: 'Set a budget in that range before publishing.',
          actionButton: fixButton('settings', draft, 'Open Settings'),
        }
      case 'invalidDates':
        return {
          title: 'Campaign Dates Need Fixing',
          message: `Campaign ${blocker.reason}`,
          details: 'Pick a start and end date before publishing.',
          actionButton: fixButton('settings', draft, 'Set Dates'),
        }
      case 'missingAudiences':
        return {
          title: 'Missing Audiences',
          message: 'You must attach audiences to this campaign',
          details:
            'If you have default campaigns configured, they will be automatically assigned to the campaigns you create in a sprint',
          actionButton: fixButton('audiences', draft, 'Attach Audiences'),
        }
      case 'unavailableAudiences':
        return {
          title: 'Unavailable Audiences',
          message:
            'Replace audiences that are unavailable in the connected ad account before publishing',
          details: `${blocker.audiences
            .map(describeUnavailableAudience)
            .join(
              ', '
            )} cannot be used with the connected ad account, so this campaign would target the wrong people.`,
          actionButton: fixButton('audiences', draft, 'Fix Audiences'),
        }
      case 'incompleteCreative':
        return {
          title: `${blocker.creative.name}`,
          message: 'Missing required properties',
          details:
            'Every creative must have media, name, website url, display url, description, and primary text.',
          actionButton: (
            <Button
              variant={'contained'}
              onClick={() =>
                history.push(
                  `/${businessId}/sprints?sprintId=${sprintId}&ad=${blocker.creative.id}`
                )
              }
            >
              Go to Creative
            </Button>
          ),
        }
      // Both routes out, because either one resolves it: take the ad off the campaign,
      // or clear the rejection in review once the ad has been changed to answer it.
      case 'rejectedCreatives':
        return {
          title: 'Rejected By Client',
          message: `${blocker.creatives.length} ad${
            blocker.creatives.length === 1 ? '' : 's'
          } on this campaign ${
            blocker.creatives.length === 1 ? 'was' : 'were'
          } rejected by the client`,
          details: `${blocker.creatives
            .map(creative => creative.name)
            .join(
              ', '
            )}. Remove them from the campaign, or clear the rejection in review once the ad answers it.`,
          actionButton: (
            <div className="flex gap-2">
              {fixButton('creatives', draft, 'Choose Ads')}
              {reviewHref && (
                // A new tab, not a navigation: the AE is mid-edit on this sprint,
                // and repairing an ad in review is a separate errand they come
                // back from. The link lands on the packet's rejected ads rather
                // than on one of them, since which to open first is their call.
                <Button
                  variant={'outlined'}
                  href={reviewHref}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Edit Ads
                </Button>
              )}
            </div>
          ),
        }
      case 'dynamicSharesGroup':
        return {
          title: 'Dynamic Ad Shares An Ad Group',
          message: `${blocker.creatives
            .map(ad => ad.name)
            .join(', ')} cannot run alongside other ads`,
          details:
            'Facebook assembles a dynamic ad from its own pool of media and copy, so it has to be the only ad in its group. Take it off this campaign, or give it a campaign of its own.',
          actionButton: fixButton('creatives', draft, 'Choose Ads'),
        }
      case 'noCreatives':
        return {
          title: 'Missing Creatives',
          message: 'You must have creatives attached to your campaign',
          details:
            'Each campaign must have either an A and a B creative or a single dynamic creative.',
          actionButton: fixButton('creatives', draft, 'Add Creatives'),
        }
      case 'emptyAdGroup':
        return {
          title: 'Empty Ad Group',
          message: `${adGroupLabel(blocker.index)} has no ads`,
          details: 'Every ad group needs at least one ad. Add ads to it, or remove the group.',
          actionButton: fixButton('creatives', draft, 'Choose Ads'),
        }
      case 'missingLeadForms':
        return {
          title: 'Missing Lead Forms',
          message: `${blocker.creatives.length} creative${
            blocker.creatives.length === 1 ? '' : 's'
          } ${blocker.creatives.length === 1 ? 'has' : 'have'} no lead form attached`,
          details:
            'This campaign collects leads on the ad, so every creative needs a lead form attached before it can publish.',
          actionButton: fixButton('leadForms', draft, 'Assign Lead Forms'),
        }
      case 'missingCallNow':
        return {
          title: 'Missing Call Now Button',
          message: `${blocker.creatives.length} creative${
            blocker.creatives.length === 1 ? '' : 's'
          } ${
            blocker.creatives.length === 1 ? "isn't a Call Now ad" : "aren't Call Now ads"
          } with a phone number`,
          details:
            'This campaign optimizes for calls, so every creative needs a Call Now button with a phone number. Set it on the creative, or choose ads that have one.',
          actionButton: fixButton('creatives', draft, 'Choose Ads'),
        }
      case 'placementsMissStory':
        return {
          title: 'Stories and Reels Are Off',
          message: 'Some ads have a Story & Reels picture this campaign will never show',
          details: PLACEMENTS_MISS_STORY_MESSAGE,
          actionButton: fixButton('settings', draft, 'Open Settings'),
        }
    }
  }

  return (
    <DataTable<DraftCampaign>
      key={`draft-campaigns-${isLoadingData ? 'loading' : 'loaded'}`}
      hideColumnFilter
      hideSearch
      hidePagination
      recordsList={sortBy('id', draftCampaigns)}
      expansionPanel={{
        shouldExpand: () => true,
        render: draft => (
          <div className="flex items-center justify-center p-4 w-full">
            <CampaignDetails
              campaignDto={draft.config}
              selectedAudiences={getAudiencesById(draft.config.audienceIDs, audiences)}
              selectedCreatives={getAdCreativeGroups(
                draft.config.creativeGroups,
                sprintCreatives || []
              )}
              sprintId={sprintId}
            />
          </div>
        ),
      }}
      tableSchema={{
        Name: {
          render: draft => draft.config.campaign.name,
        },
        'Edit Settings': {
          render: draft => (
            <Button
              variant="outlined"
              onClick={() => props.openModal('settings', draft)}
              className="whitespace-nowrap"
            >
              Settings
            </Button>
          ),
        },
        'Edit Adsets': {
          render: draft => (
            <Button
              variant="outlined"
              onClick={() => props.openModal('audiences', draft)}
              className="whitespace-nowrap"
            >
              Adsets
            </Button>
          ),
        },
        // "Choose" is what this column does — which ads are on the campaign — leaving
        // "Edit Ads" to mean changing an ad itself, on the review surface. The two used
        // to share a label on this same screen.
        'Choose Ads': {
          render: draft => (
            <Button
              variant="outlined"
              onClick={() => props.openModal('creatives', draft)}
              className="whitespace-nowrap"
            >
              Ads
            </Button>
          ),
        },
        'Edit Lead Forms': {
          render: draft =>
            resolveOptimizationDefinition(draft.config.campaign)?.requiresLeadCard ? (
              <Button
                variant="outlined"
                onClick={() => props.openModal('leadForms', draft)}
                className="whitespace-nowrap"
              >
                Lead Forms
              </Button>
            ) : null,
        },
        Status: {
          render: draft => {
            // Don't render status if data is still loading
            if (isLoadingData) {
              return <p className="text-gray-500 px-4 py-2">Loading...</p>
            }

            const errors = getCampaignPublishBlockers(draft.config, sprintCreatives, audiences).map(
              blocker => blockerError(blocker, draft)
            )

            if (errors.length > 0)
              return (
                <ErrorsIndicator
                  errors={errors}
                  drawerTitle={`${draft.config.campaign.name} has errors`}
                  drawerDescription="If you attempted to upload this to facebook, it would not work correctly."
                />
              )

            return (
              <p className="bg-mm-success-100 text-mm-success-800 inline-flex max-w-max items-center whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium">
                <Check fontSize="small" className="mr-1" />
                Ready
              </p>
            )
          },
        },
      }}
    />
  )
}
